/**
 * Landing section `#room` — Design Room (PLAN_DESIGN_ROOM_TIM.md §2 F4, RISET_DESIGN_ROOM_TIM.md §5.1–5.2, §6.2).
 * Sits between Platform and Proof (`landing-page.tsx`). Same `.mk` vocabulary as the other sections; the six-step
 * mock is CSS only (no screenshot — the real room UI is still F1/F2 work in a parallel session).
 */
import type { LucideIcon } from 'lucide-react'
import { EyeOff, GitBranch, Link2, Lock, ShieldQuestion } from 'lucide-react'
import { cn } from '#/lib/utils'
import { m } from '#/paraglide/messages'
import { Container, FeatureCol, MkLink, Overline, SectionHead } from './mk'

// ---------------------------------------------------------------- stepper + salon-booking card mock

function Stepper() {
  const steps = [
    m.dr_room_step1(),
    m.dr_room_step2(),
    m.dr_room_step3(),
    m.dr_room_step4(),
    m.dr_room_step5(),
    m.dr_room_step6(),
  ]
  const activeIndex = 3 // "Ide (brainwriting)" — where the card mock below lives
  return (
    <ol className="grid grid-cols-3 gap-2 sm:grid-cols-6">
      {steps.map((label, i) => (
        <li
          key={label}
          className={cn(
            'flex flex-col items-center gap-1.5 rounded-lg border px-2 py-3 text-center',
            i === activeIndex
              ? 'border-[var(--mk-fg)] bg-[var(--mk-fg)] text-white'
              : 'border-[var(--mk-border)] bg-white text-[var(--mk-fg)]',
          )}
        >
          <span
            className={cn(
              'font-mono text-[11px]',
              i === activeIndex ? 'text-white/70' : 'text-[var(--mk-fg-faint)]',
            )}
          >
            0{i + 1}
          </span>
          <span className="text-[13px] leading-tight font-medium">{label}</span>
        </li>
      ))}
    </ol>
  )
}

function IdeaCard({ text }: { text: string }) {
  return (
    <li className="mk-card-white flex min-h-[84px] items-center rounded-lg p-3 text-[13px] leading-snug text-[var(--mk-fg)]">
      {text}
    </li>
  )
}

/** Brainwriting: not everyone has revealed yet — this card is written but stays behind a striped pattern. */
function HiddenIdeaCard() {
  return (
    <li
      className="relative flex min-h-[84px] flex-col items-center justify-center gap-1.5 overflow-hidden rounded-lg border border-[var(--mk-border)] p-3 text-center"
      style={{
        backgroundImage:
          'repeating-linear-gradient(135deg, var(--mk-bg-muted) 0px, var(--mk-bg-muted) 8px, #ffffff 8px, #ffffff 16px)',
      }}
      aria-label={m.dr_room_card_hidden()}
    >
      <EyeOff className="size-4 text-[var(--mk-fg-faint)]" aria-hidden />
      <span className="text-[12px] font-medium text-[var(--mk-fg-muted)]">
        {m.dr_room_card_hidden()}
      </span>
      <span className="text-[11px] text-[var(--mk-fg-faint)]">
        {m.dr_room_card_hidden_hint()}
      </span>
    </li>
  )
}

function FacilitatorPanel() {
  const buttons = [
    m.dr_room_fac_btn1(),
    m.dr_room_fac_btn2(),
    m.dr_room_fac_btn3(),
  ]
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-[var(--mk-border)] bg-[var(--mk-bg-muted)] px-4 py-3">
      <span className="flex items-center gap-2 text-[13px] font-medium text-[var(--mk-fg-muted)]">
        <Lock className="size-3.5 shrink-0" aria-hidden />
        {m.dr_room_facilitator_label()}
      </span>
      <span className="flex flex-wrap justify-end gap-1.5">
        {buttons.map((b) => (
          <span
            key={b}
            className="cursor-not-allowed rounded-md border border-[var(--mk-border)] bg-white px-2.5 py-1 text-[12px] text-[var(--mk-fg-faint)]"
          >
            {b}
          </span>
        ))}
      </span>
    </div>
  )
}

function RoomMock() {
  return (
    <div className="mk-card-white flex flex-col gap-5 p-5 shadow-[0_24px_60px_-30px_rgba(15,17,21,0.3)] sm:p-7">
      <p className="text-xs text-[var(--mk-fg-muted)]">{m.mk_mock_label()}</p>
      <Stepper />
      <div>
        <Overline className="mb-2.5">{m.dr_room_mock_label()}</Overline>
        <ul className="grid gap-2.5 sm:grid-cols-4">
          <IdeaCard text={m.dr_room_card1()} />
          <IdeaCard text={m.dr_room_card2()} />
          <IdeaCard text={m.dr_room_card3()} />
          <HiddenIdeaCard />
        </ul>
      </div>
      <FacilitatorPanel />
    </div>
  )
}

// ---------------------------------------------------------------- three value columns + one fact row

function FactRow() {
  const facts = [m.dr_room_fact1(), m.dr_room_fact2(), m.dr_room_fact3()]
  return (
    <ul className="grid gap-4 border-t border-[var(--mk-border)] pt-8 sm:grid-cols-3">
      {facts.map((f) => (
        <li
          key={f}
          className="text-[13px] leading-relaxed text-[var(--mk-fg-muted)]"
        >
          {f}
        </li>
      ))}
    </ul>
  )
}

export function RoomSection() {
  const values: Array<{ icon: LucideIcon; title: string; desc: string }> = [
    { icon: Link2, title: m.dr_room_val1_title(), desc: m.dr_room_val1_desc() },
    {
      icon: ShieldQuestion,
      title: m.dr_room_val2_title(),
      desc: m.dr_room_val2_desc(),
    },
    {
      icon: GitBranch,
      title: m.dr_room_val3_title(),
      desc: m.dr_room_val3_desc(),
    },
  ]
  return (
    <section id="room" className="scroll-mt-20 py-24 lg:py-32">
      <Container className="flex flex-col gap-14">
        <SectionHead
          overline={m.dr_room_overline()}
          title={m.dr_room_title()}
          lead={m.dr_room_lead()}
          actions={
            <>
              <MkLink to="/design-room">{m.dr_room_cta_primary()}</MkLink>
              <MkLink
                to="/sign-in"
                search={{ redirect: '/home' }}
                variant="secondary"
                arrow
              >
                {m.dr_room_cta_secondary()}
              </MkLink>
            </>
          }
        />

        <div className="grid gap-10 sm:grid-cols-3">
          {values.map(({ icon: I, title, desc }) => (
            <FeatureCol
              key={title}
              title={
                <span className="flex items-center gap-2">
                  <I className="size-4 text-[var(--mk-accent)]" aria-hidden />
                  {title}
                </span>
              }
            >
              {desc}
            </FeatureCol>
          ))}
        </div>
        <details className="mk-room-details">
          <summary>{m.mk_room_example()}</summary>
          <div className="mt-6 flex flex-col gap-6">
            <RoomMock />
            <FactRow />
          </div>
        </details>
      </Container>
    </section>
  )
}
